import { Head, Link } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { route } from '../../utils/route';

interface RequiredProps {
  user: {
    email: string;
  };
}

export default function Required({ user }: RequiredProps) {
  const handleReload = () => {
    window.location.reload();
  };

  return (
    <MainLayout>
      <Head>
        <title>Verificá tu email | Abasto Virtual</title>
        <meta name="robots" content="noindex" />
      </Head>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-50 pb-16 pt-10 md:pt-16">
        <div className="mx-auto max-w-[500px] px-4">
          <div className="mt-6 rounded-2xl bg-white p-8 text-center shadow-lg md:p-10">
            {/* Email icon with animation */}
            <div className="mx-auto mb-7 flex h-24 w-24 animate-bounce items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 md:h-[100px] md:w-[100px]">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="h-12 w-12 text-white md:h-[50px] md:w-[50px]"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>

            <h1 className="mb-3 text-2xl font-bold text-blue-800 md:text-[26px]">
              Verificá tu email
            </h1>
            <p className="mb-6 text-base leading-relaxed text-gray-600">
              Te enviamos un correo de verificación para activar tu cuenta.
            </p>

            {/* Email box */}
            <div className="mb-6 flex items-center justify-center gap-2.5 rounded-xl bg-slate-100 px-5 py-4">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="h-5 w-5 flex-shrink-0 text-slate-500"
              >
                <circle cx="12" cy="12" r="4" />
                <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94" />
              </svg>
              <span className="break-all text-[15px] font-semibold text-slate-700">
                {user.email}
              </span>
            </div>

            {/* Steps box */}
            <div className="mb-7 rounded-xl bg-yellow-50 p-5 text-left">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-yellow-800">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-[18px] w-[18px] text-yellow-600"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                Pasos a seguir
              </div>
              <ul className="m-0 list-none p-0">
                <li className="flex items-start gap-3 py-2 text-sm text-yellow-900">
                  <span className="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full bg-yellow-200 text-xs font-bold text-yellow-800">
                    1
                  </span>
                  <span>Revisá tu casilla de correo (también spam)</span>
                </li>
                <li className="flex items-start gap-3 py-2 text-sm text-yellow-900">
                  <span className="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full bg-yellow-200 text-xs font-bold text-yellow-800">
                    2
                  </span>
                  <span>Hacé clic en el link de verificación</span>
                </li>
                <li className="flex items-start gap-3 pb-0 pt-2 text-sm text-yellow-900">
                  <span className="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full bg-yellow-200 text-xs font-bold text-yellow-800">
                    3
                  </span>
                  <span>Volvé a esta página y recargala</span>
                </li>
              </ul>
            </div>

            {/* Reload button */}
            <button
              type="button"
              onClick={handleReload}
              className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl bg-blue-500 px-7 py-3.5 text-base font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-blue-600 hover:shadow-lg hover:shadow-blue-500/30"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="h-5 w-5"
              >
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              Ya verifiqué, recargar página
            </button>

            <p className="mt-5 text-[13px] leading-relaxed text-gray-400">
              ¿No recibiste el correo? Revisá tu carpeta de spam o{' '}
              <Link
                href={route('profile')}
                className="text-blue-500 hover:underline"
              >
                actualizá tu email
              </Link>{' '}
              si es incorrecto.
            </p>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
